import React, { useState, useEffect } from 'react';
import { Box, TextField, Card, CardContent, Typography, Stack, Chip, Link as MuiLink } from '@mui/material';
import { Link as LinkIcon, VideoLibrary, Description, Image as ImageIcon, OpenInNew } from '@mui/icons-material';

const VistaEnlace = ({ data }) => {
    const [preview, setPreview] = useState(null);
    const [linkType, setLinkType] = useState(null);

    const detectLinkType = (url) => {
        if (!url) return null;

        const videoPatterns = [
            /youtube\.com\/watch/i,
            /youtu\.be\//i,
            /vimeo\.com/i,
            /dailymotion\.com/i,
            /\.mp4$/i,
            /\.webm$/i,
            /\.ogg$/i
        ];

        const documentPatterns = [
            /\.pdf$/i,
            /\.doc$/i,
            /\.docx$/i,
            /\.xls$/i,
            /\.xlsx$/i,
            /\.ppt$/i,
            /\.pptx$/i,
            /docs\.google\.com\/document/i,
            /docs\.google\.com\/presentation/i,
            /drive\.google\.com\/file/i
        ];

        const imagePatterns = [/\.jpg$/i, /\.jpeg$/i, /\.png$/i, /\.gif$/i, /\.webp$/i, /\.svg$/i];

        if (videoPatterns.some((pattern) => pattern.test(url))) return 'video';
        if (documentPatterns.some((pattern) => pattern.test(url))) return 'document';
        if (imagePatterns.some((pattern) => pattern.test(url))) return 'image';
        return 'link';
    };

    const getEmbedUrl = (url) => {
        if (!url) return null;

        const youtubeMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
        if (youtubeMatch) {
            return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
        }

        const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
        if (vimeoMatch) {
            return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
        }

        return url;
    };

    const getLinkTypeIcon = () => {
        switch (linkType) {
            case 'video':
                return <VideoLibrary />;
            case 'document':
                return <Description />;
            case 'image':
                return <ImageIcon />;
            default:
                return <LinkIcon />;
        }
    };

    const getLinkTypeLabel = () => {
        switch (linkType) {
            case 'video':
                return 'Video';
            case 'document':
                return 'Documento';
            case 'image':
                return 'Imagen';
            default:
                return 'Enlace';
        }
    };

    const generatePreview = (url) => {
        const type = detectLinkType(url);
        setLinkType(type);

        if (!url || !type) {
            setPreview(null);
            return;
        }

        switch (type) {
            case 'video':
                const embedUrl = getEmbedUrl(url);
                setPreview(
                    <Box sx={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden' }}>
                        <iframe
                            title="Vista previa de video"
                            src={embedUrl}
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                border: 0,
                                borderRadius: '8px'
                            }}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    </Box>
                );
                break;

            case 'document':
                if (url.toLowerCase().endsWith('.pdf')) {
                    setPreview(
                        <Box sx={{ position: 'relative', paddingBottom: '75%', height: 0, overflow: 'hidden' }}>
                            <iframe
                                title="Vista previa de documento PDF"
                                src={url}
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    border: 0,
                                    borderRadius: '8px'
                                }}
                            />
                        </Box>
                    );
                } else if (url.includes('docs.google.com/document') && url.includes('/edit')) {
                    const embedUrl = url.replace('/edit', '/preview');
                    setPreview(
                        <Box sx={{ position: 'relative', paddingBottom: '75%', height: 0, overflow: 'hidden' }}>
                            <iframe
                                title="Vista previa de documento"
                                src={embedUrl}
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    border: 0,
                                    borderRadius: '8px'
                                }}
                            />
                        </Box>
                    );
                } else if (url.includes('drive.google.com/file')) {
                    const fileIdMatch = url.match(/\/file\/d\/([^/]+)/);
                    if (fileIdMatch) {
                        const fileId = fileIdMatch[1];
                        const previewUrl = `https://drive.google.com/file/d/${fileId}/preview`;
                        setPreview(
                            <Box sx={{ position: 'relative', paddingBottom: '75%', height: 0, overflow: 'hidden' }}>
                                <iframe
                                    title="Vista previa de documento"
                                    src={previewUrl}
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        border: 0,
                                        borderRadius: '8px'
                                    }}
                                />
                            </Box>
                        );
                    }
                } else {
                    setPreview(
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'action.hover', borderRadius: '8px' }}>
                            <Description color="primary" />
                            <Box sx={{ flex: 1, overflow: 'hidden' }}>
                                <Typography variant="body2" noWrap>
                                    {url}
                                </Typography>
                            </Box>
                        </Box>
                    );
                }
                break;

            case 'image':
                setPreview(
                    <Box
                        component="img"
                        src={url}
                        alt="Preview"
                        sx={{
                            width: '100%',
                            maxHeight: '400px',
                            objectFit: 'contain',
                            borderRadius: '8px'
                        }}
                    />
                );
                break;

            case 'link':
                setPreview(
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'action.hover', borderRadius: '8px' }}>
                        <LinkIcon color="primary" />
                        <Box sx={{ flex: 1, overflow: 'hidden' }}>
                            <Typography variant="body2" noWrap>
                                {url}
                            </Typography>
                        </Box>
                    </Box>
                );
                break;
        }
    };

    useEffect(() => {
        if (data?.enlace) {
            generatePreview(data.enlace);
        }
    }, [data]);

    return (
        <Box>
            <Stack spacing={2}>
                {/* Información del material */}
                <Card variant="outlined">
                    <CardContent>
                        <Stack spacing={2}>
                            <Box>
                                <Typography variant="caption" color="text.secondary" gutterBottom>
                                    Título
                                </Typography>
                                <Typography variant="h6">{data?.titulo}</Typography>
                            </Box>

                            <Box>
                                <Typography variant="caption" color="text.secondary" gutterBottom>
                                    Tipo de material
                                </Typography>
                                <Chip label="Enlace" icon={<LinkIcon />} color="primary" size="small" />
                            </Box>

                            <Box>
                                <Typography variant="caption" color="text.secondary" gutterBottom>
                                    URL del recurso
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                    {linkType && (
                                        <Chip
                                            icon={getLinkTypeIcon()}
                                            label={getLinkTypeLabel()}
                                            size="small"
                                            color="secondary"
                                            variant="outlined"
                                        />
                                    )}
                                    <MuiLink
                                        href={data?.enlace}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            wordBreak: 'break-all'
                                        }}
                                    >
                                        {data?.enlace}
                                        <OpenInNew fontSize="small" />
                                    </MuiLink>
                                </Box>
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>

                {/* Vista previa */}
                {preview && (
                    <Card>
                        <CardContent>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Vista previa
                            </Typography>
                            {preview}
                        </CardContent>
                    </Card>
                )}
            </Stack>
        </Box>
    );
};

export default VistaEnlace;
